import { useState } from 'react';
import { apiClient } from '../api/client';

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface UseFileUploadReturn {
  upload: (file: File, folder: 'waste' | 'menu' | 'profile' | 'receipts') => Promise<string>;
  uploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
}

export function useFileUpload(): UseFileUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async (
    file: File,
    folder: 'waste' | 'menu' | 'profile' | 'receipts'
  ): Promise<string> => {
    setUploading(true);
    setProgress(null);
    setError(null);

    try {
      // Get presigned URL from backend
      const response = await apiClient.post('/api/v1/upload/presigned-url', {
        filename: file.name,
        folder,
        contentType: file.type,
      });

      if (!response.data.success) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = response.data.data;

      // Upload directly to R2 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setProgress({ loaded: file.size, total: file.size, percentage: 100 });
      setUploading(false);

      return fileUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setUploading(false);
      throw new Error(errorMessage);
    }
  };

  return {
    upload,
    uploading,
    progress,
    error,
  };
}

// Alternative: Direct upload through backend (for smaller files or when presigned URL is not preferred)
export function useDirectFileUpload(): UseFileUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async (
    file: File,
    folder: 'waste' | 'menu' | 'profile' | 'receipts'
  ): Promise<string> => {
    setUploading(true);
    setProgress(null);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await apiClient.post('/api/v1/upload/direct', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.data.success) {
        throw new Error('Failed to upload file');
      }

      const { url } = response.data.data;

      setProgress({ loaded: file.size, total: file.size, percentage: 100 });
      setUploading(false);

      return url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setUploading(false);
      throw new Error(errorMessage);
    }
  };

  return {
    upload,
    uploading,
    progress,
    error,
  };
}
